import { idbDelete, idbGet, idbGetAll, idbPut, STORES } from './database';
import { contentHash } from '../nlp/content-hash';
import type { ContextBlock } from '../pipeline/types';
import type { OptimizationSettings } from '../pipeline/settings';

const KEY_RUNS = 'benchmarkRuns';
/** Bounds total storage regardless of how many benchmarks get run — oldest runs (and any context
 * blobs no longer referenced by a retained run) are dropped on every save. */
const MAX_RETAINED_RUNS = 20;

export interface StoredContextBlockRef {
  id: string;
  label: string;
  hash: string;
  /** Original content length, so the history list can show size without resolving the blob. */
  length: number;
}

export interface BenchmarkVariantRecord {
  label: string;
  contextRefs: StoredContextBlockRef[];
  /** The fully-assembled request text for this variant (draftText() of its DraftRequest) — distinct
   * from contextRefs, which are only the context blocks. Absent on runs saved before this field existed. */
  promptProcessed?: string;
  tokensBefore: number;
  tokensAfter: number;
  responseText: string;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  elapsedMs: number;
}

export interface BenchmarkRunRecord {
  id: string;
  createdAt: number;
  prompt: string;
  model: string;
  /** Snapshot of the optimization settings active for this run, so a historical comparison stays
   * interpretable after the user changes their toggles. Absent on runs saved before this field existed. */
  settings?: OptimizationSettings;
  optimized: BenchmarkVariantRecord;
  baseline: BenchmarkVariantRecord;
}

export interface VariantInput {
  /** Display title for this variant, e.g. "Baseline (naive, matched @ 3200 tok)" — shown as-is in history. */
  label: string;
  contextBlocks: ContextBlock[];
  /** See BenchmarkVariantRecord.promptProcessed — the caller supplies this (draftText(draft)); it isn't derived here. */
  promptProcessed?: string;
  tokensBefore: number;
  tokensAfter: number;
  responseText: string;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  elapsedMs: number;
}

/**
 * Content-addressed storage for context blocks: identical content (e.g. the same open file sent
 * across several consecutive benchmark runs) is written once regardless of how many runs use it —
 * the run record itself only stores a hash reference, not the text.
 */
async function storeContextBlocks(blocks: ContextBlock[]): Promise<StoredContextBlockRef[]> {
  const refs: StoredContextBlockRef[] = [];
  for (const block of blocks) {
    const hash = await contentHash(block.content);
    // benchmarkContext uses keyPath: 'hash' — the key comes from the object itself, no explicit key arg.
    await idbPut(STORES.benchmarkContext, { hash, content: block.content });
    refs.push({ id: block.id, label: block.label, hash, length: block.content.length });
  }
  return refs;
}

function toVariantRecord(input: VariantInput, contextRefs: StoredContextBlockRef[]): BenchmarkVariantRecord {
  return {
    label: input.label,
    contextRefs,
    promptProcessed: input.promptProcessed,
    tokensBefore: input.tokensBefore,
    tokensAfter: input.tokensAfter,
    responseText: input.responseText,
    inputTokens: input.inputTokens,
    outputTokens: input.outputTokens,
    costUsd: input.costUsd,
    elapsedMs: input.elapsedMs,
  };
}

export async function saveBenchmarkRun(input: {
  prompt: string;
  model: string;
  settings?: OptimizationSettings;
  optimized: VariantInput;
  baseline: VariantInput;
}): Promise<void> {
  const [optimizedRefs, baselineRefs] = await Promise.all([
    storeContextBlocks(input.optimized.contextBlocks),
    storeContextBlocks(input.baseline.contextBlocks),
  ]);

  const record: BenchmarkRunRecord = {
    id: crypto.randomUUID(),
    createdAt: Date.now(),
    prompt: input.prompt,
    model: input.model,
    settings: input.settings,
    optimized: toVariantRecord(input.optimized, optimizedRefs),
    baseline: toVariantRecord(input.baseline, baselineRefs),
  };

  const existing = await getBenchmarkRuns();
  const retained = [record, ...existing].slice(0, MAX_RETAINED_RUNS);
  await idbPut(STORES.settings, retained, KEY_RUNS);
  await pruneUnreferencedContext(retained);
}

export async function getBenchmarkRuns(): Promise<BenchmarkRunRecord[]> {
  return (await idbGet<BenchmarkRunRecord[]>(STORES.settings, KEY_RUNS)) ?? [];
}

export async function resolveContextBlock(hash: string): Promise<string | undefined> {
  const row = await idbGet<{ hash: string; content: string }>(STORES.benchmarkContext, hash);
  return row?.content;
}

/** Deletes any stored context blob no longer referenced by a retained run — otherwise evicted runs' blobs leak forever. */
async function pruneUnreferencedContext(retainedRuns: BenchmarkRunRecord[]): Promise<void> {
  const referenced = new Set<string>();
  for (const run of retainedRuns) {
    for (const ref of [...run.optimized.contextRefs, ...run.baseline.contextRefs]) referenced.add(ref.hash);
  }
  const allBlobs = await idbGetAll<{ hash: string }>(STORES.benchmarkContext);
  for (const blob of allBlobs) {
    if (!referenced.has(blob.hash)) await idbDelete(STORES.benchmarkContext, blob.hash);
  }
}
